import React from 'react';

interface StatMetaItem {
  label: string;
  value: string;
}

const Overview: React.FC = () => {
  const timeTabs = ['最近7天', '最近30天', '本月', '上月', '本年', '自定义'];

  const stats = [
    {
      title: '活跃用户',
      value: '519',
      meta: [
        { label: '新用户', value: '63' },
        { label: '总人数', value: '1411' },
        { label: '当前参会人数', value: '56' }
      ] as StatMetaItem[],
      trend: [16, 24, 18, 28, 26, 32, 25],
      color: '#34c38f'
    },
    {
      title: '会议数量',
      value: '284',
      meta: [
        { label: '总时长', value: '271.66小时' },
        { label: '参会人数', value: '3979' },
        { label: '质量正常会议占比', value: '99.6%' }
      ] as StatMetaItem[],
      trend: [10, 18, 14, 22, 16, 26, 20],
      color: '#3b82f6'
    },
    {
      title: '会议质量',
      value: '好 (99)',
      meta: [
        { label: '音频平均卡顿率', value: '0.37%' },
        { label: '屏幕共享平均卡顿率', value: '0.25%' },
        { label: '平均延迟', value: '430ms' }
      ] as StatMetaItem[],
      trend: [14, 20, 16, 24, 22, 26, 19],
      color: '#f6c343'
    }
  ];

  const durationTop = [
    { name: '刘一鸣', value: 2854 },
    { name: '刘鹏', value: 1297 },
    { name: '胡婷婷', value: 589 },
    { name: '谢峰', value: 554 },
    { name: '胡捷', value: 505 },
    { name: '李衡', value: 478 },
    { name: '唐鹏', value: 415 },
    { name: '沈琪', value: 398 },
    { name: '杨靖', value: 392 },
    { name: '王鑫', value: 373 }
  ];

  const deviceDistribution = [
    { label: 'Windows 1769', value: 44.5, color: '#4f75ff' },
    { label: 'Mac 1490', value: 37.4, color: '#7bd389' },
    { label: 'iOS 436', value: 11.6, color: '#f6c343' },
    { label: 'Android 172', value: 6.2, color: '#ff8c4b' },
    { label: 'web 79', value: 0.2, color: '#6f8fe9' },
    { label: 'Harmony 6', value: 0.1, color: '#a871ff' }
  ];

  const meetingTrend = [
    { date: '2026-02-01', value: 74 },
    { date: '2026-02-02', value: 70 },
    { date: '2026-02-03', value: 66 },
    { date: '2026-02-04', value: 60 },
    { date: '2026-02-05', value: 63 },
    { date: '2026-02-06', value: 48 },
    { date: '2026-02-07', value: 44 },
    { date: '2026-02-08', value: 62 },
    { date: '2026-02-09', value: 55 },
    { date: '2026-02-10', value: 47 }
  ];

  const durationDistribution = [
    { label: '5分钟以下', value: 25.35, color: '#4f75ff' },
    { label: '5-40分钟', value: 50.71, color: '#22c1c3' },
    { label: '40-90分钟', value: 15.49, color: '#f6c343' },
    { label: '90分钟以上', value: 8.45, color: '#f18235' }
  ];

  const meetingTypes = [
    { label: '传统音频会议', value: 92, rate: '32.39%', color: '#4f75ff' },
    { label: '普通视频会议', value: 12, rate: '4.23%', color: '#22c1c3' },
    { label: '异地屏幕共享会议', value: 186, rate: '65.49%', color: '#f6c343' },
    { label: '同步音视频共享', value: 6, rate: '2.11%', color: '#f18235' }
  ];

  const maxDuration = Math.max(...durationTop.map(item => item.value));
  const maxMeetingType = Math.max(...meetingTypes.map(item => item.value));
  const maxTrend = Math.max(...meetingTrend.map(item => item.value));

  const buildSparkline = (values: number[]) => {
    const width = 120;
    const height = 44;
    const padding = 4;
    const max = Math.max(...values);
    const min = Math.min(...values);
    return values
      .map((value, index) => {
        const x = (width - padding * 2) * (index / (values.length - 1)) + padding;
        const ratio = max === min ? 0.5 : (value - min) / (max - min);
        const y = height - padding - ratio * (height - padding * 2);
        return `${x},${y}`;
      })
      .join(' ');
  };

  const buildPieGradient = (items: Array<{ value: number; color: string }>) => {
    let current = 0;
    const segments = items.map(item => {
      const start = current;
      const end = current + item.value;
      current = end;
      return `${item.color} ${start}% ${end}%`;
    });
    return `conic-gradient(${segments.join(', ')})`;
  };

  const lineWidth = 1000;
  const lineHeight = 180;
  const linePadding = 28;
  const chartPoints = meetingTrend.map((item, index) => {
    const x = (lineWidth - linePadding * 2) * (index / (meetingTrend.length - 1)) + linePadding;
    const ratio = item.value / maxTrend;
    const y = lineHeight - linePadding - ratio * (lineHeight - linePadding * 2);
    return { x, y };
  });

  const buildSmoothPath = (points: Array<{ x: number; y: number }>) => {
    if (points.length === 0) {
      return '';
    }

    const smoothing = 0.2;
    const controlPoint = (
      current: { x: number; y: number },
      previous?: { x: number; y: number },
      next?: { x: number; y: number },
      reverse?: boolean
    ) => {
      const p = previous || current;
      const n = next || current;
      const angle = Math.atan2(n.y - p.y, n.x - p.x) + (reverse ? Math.PI : 0);
      const length = Math.hypot(n.x - p.x, n.y - p.y) * smoothing;
      return {
        x: current.x + Math.cos(angle) * length,
        y: current.y + Math.sin(angle) * length
      };
    };

    let d = `M ${points[0].x},${points[0].y}`;
    for (let i = 0; i < points.length - 1; i += 1) {
      const current = points[i];
      const next = points[i + 1];
      const cp1 = controlPoint(current, points[i - 1], next);
      const cp2 = controlPoint(next, current, points[i + 2], true);
      d += ` C ${cp1.x},${cp1.y} ${cp2.x},${cp2.y} ${next.x},${next.y}`;
    }
    return d;
  };

  return (
    <div className="overview-page">
      <div className="page-title">项目概览</div>
      <div className="overview-filterbar">
        <div className="tab-list">
          {timeTabs.map((tab, index) => (
            <button
              key={tab}
              type="button"
              className={`tab-button ${index === 0 ? 'active' : ''}`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      <div className="overview-grid">
        {stats.map(item => (
          <div key={item.title} className="card stat-panel">
            <div className="stat-header">
              <span>{item.title}</span>
              <span className="stat-tag">近7天</span>
            </div>
            <div className="stat-body">
              <div className="stat-value">{item.value}</div>
              {item.trend ? (
                <svg className="sparkline" viewBox="0 0 120 44">
                  <polyline
                    points={buildSparkline(item.trend)}
                    fill="none"
                    stroke={item.color}
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              ) : (
                <div className="sparkline-placeholder" />
              )}
            </div>
            <div className="stat-meta">
              {item.meta.map(meta => (
                <span key={`${item.title}-${meta.label}`} className="stat-meta-item">
                  <span className="meta-label">{meta.label}</span>
                  <span className="meta-value">{meta.value}</span>
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="overview-grid">
        <div className="card">
          <div className="card-title">会议时长TOP10用户</div>
          <div className="bar-chart">
            {durationTop.map(item => (
              <div key={item.name} className="bar-row">
                <div className="bar-label">{item.name}</div>
                <div className="bar-track">
                  <div
                    className="bar-fill"
                    style={{ width: `${(item.value / maxDuration) * 100}%` }}
                  />
                </div>
                <div className="bar-value">{item.value}分钟</div>
              </div>
            ))}
          </div>
        </div>
        <div className="card">
          <div className="card-title">会议用户设备分布</div>
          <div className="pie-layout">
            <div className="pie-chart" style={{ background: buildPieGradient(deviceDistribution) }} />
            <div className="legend">
              {deviceDistribution.map(item => (
                <div key={item.label} className="legend-item">
                  <span className="legend-dot" style={{ backgroundColor: item.color }} />
                  <span>{item.label}</span>
                  <span className="legend-value">{item.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-title">会议数量</div>
        <div className="line-chart">
          <svg viewBox={`0 0 ${lineWidth} ${lineHeight}`} preserveAspectRatio="none">
            <path
              d={buildSmoothPath(chartPoints)}
              fill="none"
              stroke="#4f75ff"
              strokeWidth="1.6"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
            {meetingTrend.map((item, index) => (
              <circle
                key={item.date}
                cx={chartPoints[index].x}
                cy={chartPoints[index].y}
                r="2"
                fill="#4f75ff"
              />
            ))}
          </svg>
          <div className="line-labels">
            {meetingTrend.map((item, index) => {
              const left = `${(chartPoints[index].x / lineWidth) * 100}%`;
              const transform = index === 0 ? 'translateX(0)' : index === meetingTrend.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)';
              return (
                <span key={item.date} style={{ left, transform }}>
                  {item.date}
                </span>
              );
            })}
          </div>
        </div>
      </div>

      <div className="overview-grid">
        <div className="card">
          <div className="card-title">会议时长分布占比</div>
          <div className="donut-layout">
            <div
              className="donut"
              style={{ background: buildPieGradient(durationDistribution) }}
            />
            <div className="legend">
              {durationDistribution.map(item => (
                <div key={item.label} className="legend-item">
                  <span className="legend-dot" style={{ backgroundColor: item.color }} />
                  <span>{item.label}</span>
                  <span className="legend-value">{item.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-title">会议开启录制占比</div>
          <div className="donut-center">
            <div
              className="donut"
              style={{ background: 'conic-gradient(#4f75ff 0% 6.69%, #e7ecf5 6.69% 100%)' }}
            >
              <div className="donut-value">6.69%</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-title">会议类型数量分布</div>
        <div className="bar-chart-vertical">
          {meetingTypes.map(item => (
            <div key={item.label} className="bar-column">
              <div className="bar-value">{item.value} ({item.rate})</div>
              <div className="bar-outer">
                <div
                  className="bar-inner"
                  style={{
                    height: `${(item.value / maxMeetingType) * 100}%`,
                    backgroundColor: item.color
                  }}
                />
              </div>
              <div className="bar-label">{item.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Overview;
